import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { OptionQuestion } from "types/question";
import { AnswerWithReviewer } from "types/answer-with-reviewer.type";

type MultipleChoicePieChartProps = {
  question: OptionQuestion;
  answersWithReviewers: AnswerWithReviewer[];
};

// Color palette matching the Figma design - teal/blue shades
const COLORS = [
  "#14B8A6", // Teal (Strongly Agree)
  "#5EEAD4", // Light teal (Agree)
  "#99F6E4", // Very light teal (Neutral)
  "#CCFBF1", // Lightest teal (Disagree)
  "#E0F2FE", // Light blue
  "#BAE6FD", // Lighter blue
  "#7DD3FC", // Medium blue
];

const MultipleChoicePieChart: React.FC<MultipleChoicePieChartProps> = ({
  question,
  answersWithReviewers,
}) => {
  // Calculate counts for each option and create color mapping
  const { optionCounts, colorMap } = React.useMemo(() => {
    const counts: Record<number, number> = {};
    const colorMapping: Record<number, string> = {};

    // Initialize all options with 0 and assign colors
    question.answerOptions?.forEach((option, index) => {
      counts[option.id] = 0;
      colorMapping[option.id] = COLORS[index % COLORS.length];
    });

    // Count answers
    answersWithReviewers?.forEach(({ answer }) => {
      if (answer.answerOptionId) {
        counts[answer.answerOptionId] =
          (counts[answer.answerOptionId] || 0) + 1;
      }
    });

    // Transform to array format for the chart
    // Show all options, but only include non-zero values in the chart
    const countsArray = question.answerOptions.map((option) => ({
      id: option.id,
      name: option.label,
      value: counts[option.id] || 0,
    }));

    return { optionCounts: countsArray, colorMap: colorMapping };
  }, [question, answersWithReviewers]);

  const totalFeedbacks = answersWithReviewers?.length || 0;

  return (
    <div className="flex flex-row items-center gap-8">
      <div
        className="relative flex-shrink-0"
        style={{ width: 200, height: 200 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={optionCounts.filter((item) => item.value > 0)}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={1}
              dataKey="value"
              label={false}
            >
              {optionCounts
                .filter((item) => item.value > 0)
                .map((entry) => (
                  <Cell key={`cell-${entry.id}`} fill={colorMap[entry.id]} />
                ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div
              className="font-bold text-neutral-150 leading-none"
              style={{ fontSize: "40px" }}
            >
              {totalFeedbacks}
            </div>
            <div
              className="text-neutral-150 leading-tight"
              style={{ fontSize: "16px" }}
            >
              {totalFeedbacks <= 1 ? "Feedback" : "Feedbacks"}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1">
        <div className="flex flex-col gap-3">
          {optionCounts.map((item) => (
            <div key={`legend-${item.id}`} className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-sm"
                style={{ backgroundColor: colorMap[item.id] }}
              />
              <span className="text-sm text-neutral-150">
                {item.name} ({item.value})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MultipleChoicePieChart;
